/*
 * 🔒 One File row for a Files tile and a document's Files block (decision 3010),
 * in two layouts chosen by the width of the row's own box (decision 3013,
 * amending 0605). The row is a container, so a one-span tile, a column of a
 * wider tile and a phone each draw the layout their own width allows, never the
 * viewport's. Narrow is the default; wide is the container query.
 *
 * 🔒 The row is a condensed card (decision 7080, the owner's words of 2026-10-23):
 * a small icon, the text lines beside it, and a foot holding a clean scan's
 * shield at the left and the acts at the right. Narrow, the acts are small icon
 * buttons; wide, they are labelled buttons. The Tile lays cards out two or three
 * to a line by its own width.
 */
.row {
  display: flex;
  container-type: inline-size;
  padding: var(--space-1) var(--space-3);
  font-size: var(--font-size-small);
}

.row:last-child {
  padding-bottom: var(--space-3);
}

.frame {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  gap: var(--space-2);
  min-width: 0;
  padding: var(--space-2);
  border: 1px solid var(--tile-border);
  border-radius: var(--radius-small);
  background: var(--surface-disabled);
}

/* The card's head (figure 41): the icon, the text column, a quarantined file's pill. */
.box {
  display: flex;
  align-items: flex-start;
  gap: var(--space-2);
  flex: 1 1 auto;
  min-width: 0;
}

/* 🔒 The file type's icon (decision 3011), two of the row's text lines tall in the card (decision 7080). */
.kind {
  display: inline-flex;
  flex: none;
  width: var(--file-kind-size);
  height: var(--file-kind-size);
}

.kind > svg {
  width: 100%;
  height: 100%;
}

/* 🔒 The foot (decision 7080): a clean scan's mark at the left, the acts at the right, one line that wraps rather than overflowing. */
.foot {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-1) var(--space-2);
  min-width: 0;
}

.scanMark {
  display: inline-flex;
  align-items: center;
  min-width: 0;
  gap: var(--space-half);
  color: var(--status-good-text);
  font-size: var(--font-size-caption);
  font-weight: var(--font-weight-semibold);
  line-height: var(--line-height-tight);
  text-transform: uppercase;
}

.scanMark > svg {
  flex: none;
  width: var(--icon-size-inline);
  height: var(--icon-size-inline);
}

.scanner {
  overflow-wrap: anywhere;
}

/* 🔒 The text column, up to three lines (decision 3013): the kind, the name, the state. */
.text {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-width: 0;
  gap: var(--space-half);
  line-height: var(--line-height-base);
}

.kindLine {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-1);
}

.chip {
  display: inline-flex;
}

/* 🔒 The record the file came through (decision 6040): a label of its own after the type, never in the flag's or the pill's place. */
.origin {
  display: inline-flex;
  min-width: 0;
  overflow-wrap: anywhere;
}

.version {
  color: var(--text-muted);
  font-size: var(--font-size-caption);
}

.nameLine {
  display: flex;
  align-items: flex-start;
  gap: var(--space-1);
  min-width: 0;
}

/* The name is the link; a file's name breaks inside itself rather than pushing the box wider. */
.name {
  flex: 1 1 auto;
  min-width: 0;
  color: inherit;
  font-weight: var(--font-weight-semibold);
  text-decoration: none;
  overflow-wrap: anywhere;
}

a.name:hover .nameText {
  text-decoration: underline;
}

/* 🔒 Decision 7420: the lock beside an encrypted file's name, in the muted ink, never underlined with the link. */
.lock {
  display: inline-flex;
  margin-inline-end: var(--space-1);
  color: var(--text-muted);
  vertical-align: middle;
}

.stateLine {
  display: flex;
  align-items: center;
  min-width: 0;
}

/* 🔨 The description wraps inside the box rather than overflowing it. */
.detail {
  color: var(--text-muted);
  font-size: var(--font-size-caption);
}

.detailPart {
  white-space: normal;
  overflow-wrap: anywhere;
}

/* 🔒 A quarantined file's sentence (decision 1182), a warning, in the error ink. */
.quarantine {
  color: var(--status-error-text);
  font-size: var(--font-size-caption);
  overflow-wrap: anywhere;
}

/* 🔒 A verified file's last line (decision 3012): the verifier's avatar the height of a text line, a thin white ring, then the word in green at the row's own size. */
.verified {
  display: inline-flex;
  align-items: center;
  gap: var(--space-1);
  --avatar-size: var(--file-row-line-height);
}

.verifier {
  display: inline-flex;
}

.verifier [data-avatar] {
  box-shadow: 0 0 0 var(--control-border-width) var(--on-avatar);
}

.verifiedWord {
  color: var(--status-good-text);
  font-size: var(--font-size-small);
  font-weight: var(--font-weight-semibold);
}

/* A file still being scanned says so on its last line, in grey. */
.pending {
  color: var(--text-muted);
  font-size: var(--font-size-small);
}

/* Who verified and when, kept for a screen reader where the eye reads the avatar and the title. */
.srOnly {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
}

/* A quarantined file's pill at the box's right. */
.flags {
  display: inline-flex;
  align-items: center;
  flex: none;
  margin-left: auto;
}

.pill {
  display: inline-flex;
  vertical-align: middle;
}

/* 🔒 Narrow (decision 3013, placed by decision 7080): the small icon buttons at the foot's right. */
.narrowActions {
  display: inline-flex;
  flex: none;
  margin-left: auto;
  gap: var(--space-half);
}

.wideActions {
  display: none;
  align-items: center;
  flex: none;
  margin-left: auto;
  gap: var(--space-2);
}

.button {
  display: inline-flex;
}

/* 🔒 A row's pencil and the narrow row's two icon buttons are the borderless icon button (decision 1327): no box until hovered, the muted ink, navy on hover. */
.edit,
.iconButton {
  flex: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: var(--button-height-small);
  height: var(--button-height-small);
  padding: 0;
  border: 0;
  border-radius: var(--radius-small);
  background: transparent;
  color: var(--text-muted);
  cursor: pointer;
}

.edit:hover,
.iconButton:hover {
  background: var(--surface-2);
  color: var(--navy-primary);
}

.edit:focus-visible,
.iconButton:focus-visible {
  outline: var(--focus-ring);
  outline-offset: var(--focus-ring-offset);
}

/* 🔒 On a phone the icon buttons stand at the touch minimum, so the foot's acts are reachable with a thumb (decision 7080). */
@media (pointer: coarse) {
  .edit,
  .iconButton {
    width: var(--touch-target-min);
    height: var(--touch-target-min);
  }
}

/* 🔒 Wide (decision 3013): the labelled buttons stand in the foot, and the icon buttons go. */
@container (--file-row-wide) {
  .narrowActions {
    display: none;
  }

  .wideActions {
    display: inline-flex;
  }
}
